/**
 * Экран «Цели» (раздел 8, пункт 4).
 *
 * Смысл экрана — сделать главную цель осязаемой: сколько сейчас, сколько
 * надо, и когда это случится по текущему темпу. Все цифры приходят с
 * сервера: e1RM, целевой вес, прогнозная дата, темп кг/мес и R² считает
 * движок при каждом запросе (раздел 2, принцип «цифры первичны»). Клиент
 * не считает ничего, кроме доли «текущее от целевого» для полосы прогресса —
 * это оформление уже полученных чисел, а не новая метрика.
 *
 * Честность (раздел 2, принцип 3 и раздел 13, риск «прогноз врёт на малых
 * данных»): если движок сказал `has_forecast: false`, показывается его
 * `reason` как есть и никакой даты. Смягчать («примерно», «около») нельзя —
 * движок уже решил, что данных мало, и подмена его решения вежливой
 * формулировкой вернула бы ровно тот риск, ради которого порог по R² и вводился.
 */

import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { api, ApiError, NetworkError } from '../api/client'
import type {
  Flag,
  Forecast,
  Goal,
  GoalAddition,
  GoalAdditionResult,
  GoalOffer,
  GoalOption,
  GoalPace,
  Mesocycle,
  GoalTheme as GoalThemeOut,
  Today,
} from '../api/types'
import { haptic } from '../auth/telegram'
import { ExerciseHead } from '../components/ExerciseHead'
import { ExercisePeek } from '../components/ExerciseRow'
import { ExerciseThumb, type ExerciseRef } from '../components/ExerciseThumb'
import {
  ConfirmButton,
  Empty,
  ErrorBox,
  Flags,
  Loader,
  Stepper,
  formatDate,
  formatWeight,
} from '../components/ui'
import { useExercises } from '../api/exercises'
import { useAsync } from '../hooks'
import { CACHE_TODAY, readCache } from '../offline/db'

/**
 * Целевой вес по умолчанию: главная цель из спецификации — жим 100 кг.
 *
 * Используется ровно в одном случае: сервер не отдал по движению ни темпа, ни
 * текущего максимума, ни минимального веса. Во всех остальных поле
 * отталкивается от цифр сервера — см. `startingTarget`.
 */
const DEFAULT_TARGET = 100

/**
 * Границы целевого веса. Ровно те же, что у сервера (`GoalIn`: 0 < x ≤ 500) —
 * свой, более узкий коридор клиент придумывать не вправе: он молча запретил бы
 * законную цель (жим ногами на 400 кг) там, где движок её принимает.
 */
const TARGET_MIN = 2.5
const TARGET_MAX = 500

/**
 * С какого веса открывается поле цели.
 *
 * Порядок ровно такой: предложение движка (посчитано по собственному темпу
 * человека) → текущий максимум → минимальный вес снаряда (пустой гриф, первая
 * гантель, нижняя плитка стека). Все три — числа СЕРВЕРА. Своё правдоподобное
 * число клиент не выдумывает: «сто килограммов» на подъёме на бицепс — это не
 * подсказка, а насмешка.
 */
function startingTarget(option: GoalOption): number {
  if (option.suggested_target !== null) return option.suggested_target
  if (option.current_e1rm !== null) return option.current_e1rm
  return option.min_weight > 0 ? option.min_weight : DEFAULT_TARGET
}

/** Ответ «Сегодня» вместе с пометкой, свежий он или поднят из кеша. */
interface TodayView {
  data: Today
  fresh: boolean
}

/**
 * Ближайшая тренировка — источник названий упражнений и «сегодня» сервера.
 *
 * При обрыве связи берётся последний сохранённый ответ (тот же кеш, что
 * пишет экран «Сегодня»): названия из него не устаревают. Отказ сервера
 * кешем не подменяется — сервер ответил, значит связь есть.
 */
async function loadToday(): Promise<TodayView> {
  try {
    return { data: await api.get<Today>('/today'), fresh: true }
  } catch (e) {
    if (e instanceof NetworkError) {
      const cached = await readCache<Today>(CACHE_TODAY).catch(() => null)
      if (cached) return { data: cached.value, fresh: false }
    }
    throw e
  }
}

export default function Goals() {
  const goals = useAsync(() => api.get<Goal[]>('/goals'), [])
  // Отдельным запросом, а не в одном `Promise.all` с целями: названия
  // упражнений — украшение, и падение `/today` не должно прятать цели.
  const today = useAsync(loadToday, [])

  const [creating, setCreating] = useState(false)
  const [busyGoalId, setBusyGoalId] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  // Раскрытая техника — одна на экран: движение цели или доп. упражнения.
  const [peek, setPeek] = useState<string | null>(null)
  const togglePeek = (key: string) => setPeek((prev) => (prev === key ? null : key))

  // Доп. упражнения под цели — отдельным запросом: они считаются по плану, и
  // их отказ не должен прятать сами цели.
  const additions = useAsync(() => api.get<GoalAddition[]>('/goals/additions'), [])
  // В карточке открытой цели — только ответ по открытой цели: списки целей и
  // добавок приходят разными запросами и могут разойтись.
  const additionOf = useMemo(() => {
    const map = new Map<number, GoalAddition>()
    for (const item of additions.data ?? []) {
      if (item.goal_status === 'open') map.set(item.goal_id, item)
    }
    return map
  }, [additions.data])
  const additionsFailed = additions.error !== null && additions.data === null

  const blocks = today.data?.data.session?.blocks
  const names = useMemo(() => {
    const map = new Map<number, string>()
    for (const block of blocks ?? []) map.set(block.exercise.id, block.exercise.name)
    return map
  }, [blocks])

  // Полный справочник одним кешируемым запросом. Раньше имя каждой цели
  // добиралось через /progress/{id} — а это регрессия и прогноз ради одной
  // строки текста.
  const catalog = useExercises()

  /** Название упражнения. Не нашли нигде — честно говорим номер. */
  const nameOf = (exerciseId: number): string =>
    names.get(exerciseId) ?? catalog.name(exerciseId)

  const openGoals = (goals.data ?? []).filter((g) => g.status === 'open')
  const closedGoals = (goals.data ?? []).filter((g) => g.status !== 'open')
  // Цель закрыли, а добавка под неё осталась в плане — убрать её можно только
  // здесь. Цель, которую устаревший список ещё считает открытой, сюда не
  // попадает: одна добавка не должна стоять на экране дважды.
  const leftovers = (additions.data ?? []).filter(
    (a) => a.goal_status !== 'open' && !openGoals.some((g) => g.id === a.goal_id),
  )

  /** Цели и план меняются вместе: после любого действия перечитываем оба. */
  async function refresh(): Promise<void> {
    await Promise.all([goals.reload(), additions.reload()])
  }

  /** Сменить статус цели. Сервер пересчитает всё сам, поэтому — reload. */
  async function changeStatus(goal: Goal, status: 'achieved' | 'abandoned') {
    // Второе нажатие, пока первое в полёте, отправило бы второй PATCH и могло
    // бы переписать статус наоборот — «отказ» поверх «достигнута».
    if (busyGoalId !== null) return
    setBusyGoalId(goal.id)
    setActionError(null)
    try {
      await api.patch<Goal>(`/goals/${goal.id}`, { status })
      haptic(status === 'achieved' ? 'heavy' : 'light')
      await refresh()
    } catch (e) {
      setActionError(describe(e, 'Не удалось изменить цель'))
    } finally {
      setBusyGoalId(null)
    }
  }

  // Первая загрузка — Loader на весь экран. Перезапрос после действия —
  // нет: экран не должен мигать спиннером и терять раскрытую форму.
  if (goals.loading && goals.data === null) {
    return (
      <div className="screen">
        <h1>Цели</h1>
        <Loader />
      </div>
    )
  }

  if (goals.error && goals.data === null) {
    return (
      <div className="screen">
        <h1>Цели</h1>
        <ErrorBox error={goals.error} onRetry={() => void goals.reload()} />
      </div>
    )
  }

  const nothingAtAll = openGoals.length === 0 && closedGoals.length === 0

  return (
    <div className="screen">
      <h1>Цели</h1>
      <p className="muted" style={{ margin: '0 0 16px' }}>
        Цель — это вес, к которому ты идёшь. Из неё же строится прогноз даты выхода.
        Целей может быть несколько: руки, плечи, подтягивания — по одной на движение.
      </p>

      {/* Список не обновился, но старый ответ на экране остался. Молчать об
          этом нельзя: человек решит, что видит свежие цифры. */}
      {goals.error !== null && goals.data !== null && (
        <div className="flag warning">
          Список не обновился — на экране данные из прошлого ответа сервера.
        </div>
      )}
      {additions.error !== null && additions.data !== null && (
        <div className="flag warning">
          Доп. упражнения не обновились — на экране прошлый ответ сервера.
        </div>
      )}

      {actionError && <div className="flag critical">{actionError}</div>}

      {nothingAtAll && !creating && (
        <>
          <Empty
            title="Целей пока нет"
            hint="Поставь цель — и приложение начнёт считать, когда ты выйдешь на этот вес по текущему темпу. Без цели прогнозировать нечего: дата берётся из неё."
          />
          <button className="btn primary wide big" onClick={() => setCreating(true)}>
            Поставить первую цель
          </button>
        </>
      )}

      {openGoals.map((goal) => (
        <GoalCard
          key={goal.id}
          goal={goal}
          exercise={{
            id: goal.exercise_id,
            name: nameOf(goal.exercise_id),
            is_custom: catalog.byId.get(goal.exercise_id)?.is_custom,
          }}
          busy={busyGoalId === goal.id}
          peekKey={peek}
          onTogglePeek={togglePeek}
          addition={additionOf.get(goal.id) ?? null}
          additionsFailed={additionsFailed}
          onAdditionChanged={refresh}
          onAchieved={() => void changeStatus(goal, 'achieved')}
          onAbandoned={() => void changeStatus(goal, 'abandoned')}
        />
      ))}

      {leftovers.length > 0 && (
        <div className="card">
          <b>Добавки под закрытые цели</b>
          <div className="faint" style={{ marginTop: 6 }}>
            Цель закрыта, а доп. упражнение под неё осталось в плане. Сам план не
            меняется — убери добавку, если она больше не нужна.
          </div>
          {leftovers.map((item) => (
            <GoalAdditionBlock
              key={item.goal_id}
              addition={item}
              peekKey={peek}
              onTogglePeek={togglePeek}
              onChanged={refresh}
            />
          ))}
        </div>
      )}

      {!nothingAtAll && !creating && (
        <button className="btn wide" onClick={() => setCreating(true)}>
          Новая цель
        </button>
      )}

      {openGoals.length > 0 && !creating && <ApplyToProgram onRebuilt={refresh} />}

      {creating && (
        <NewGoalForm
          openGoals={openGoals}
          nameOf={nameOf}
          // Нижняя граница календаря — только из СВЕЖЕГО ответа сервера:
          // «сегодня» из кеша может быть вчерашним, и поле разрешило бы дату,
          // которую сервер всё равно отвергнет.
          minDate={today.data?.fresh ? today.data.data.date : null}
          onCancel={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false)
            await refresh()
          }}
        />
      )}

      {closedGoals.length > 0 && (
        <>
          <h2>Закрытые цели</h2>
          {closedGoals.map((goal) => (
            <ClosedGoalCard key={goal.id} goal={goal} name={nameOf(goal.exercise_id)} />
          ))}
        </>
      )}
    </div>
  )
}

/**
 * Применить цели к программе.
 *
 * Зачем кнопка вообще нужна. Мезоцикл живёт 4-6 недель, а цель влияет на план
 * в момент СБОРКИ: подбирает движение в слот и добавляет объём, если человек
 * не успевает к сроку. Без этой кнопки поставленная сегодня цель дошла бы до
 * программы через месяц — то есть выглядела бы ровно так, как выглядела
 * раньше: «поставил цель, а программа та же».
 *
 * Тело запроса ПУСТОЕ намеренно: сервер наследует у текущего мезоцикла режим,
 * число дней, длину и готовую программу. Прислать сюда «дефолты» значило бы
 * тихо переселить человека из дефицита в набор и потерять выбранную им
 * методику.
 *
 * Что изменилось, рассказывает сервер флагами — клиент их только показывает.
 * После пересборки перечитываются и доп. упражнения: план новый, и прежние
 * предложения к нему уже не относятся.
 */
function ApplyToProgram({ onRebuilt }: { onRebuilt: () => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const [flags, setFlags] = useState<Flag[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function apply(): Promise<void> {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const meso = await api.post<Mesocycle>('/mesocycle/rebuild', {})
      haptic('medium')
      setFlags(meso.flags)
      await onRebuilt()
    } catch (e) {
      setError(describe(e, 'Не удалось пересобрать программу'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <b>Подогнать программу под цели</b>
      <div className="faint" style={{ margin: '6px 0 12px' }}>
        Движок поставит движения целей в план. В программе из шаблона отстающей
        цели он сам добавит подходов в пределах недельного коридора; объём готовой
        программы не трогает. Доп. упражнения, которые ты добавил под цели,
        сохранятся, если в новом плане им найдётся место. Доп. занятие под цель,
        если оно есть в программе, соберётся заново — из целей, открытых сейчас.
        Режим, длина и выбранная программа останутся прежними.
      </div>

      {flags !== null &&
        (flags.length > 0 ? (
          <Flags flags={flags} />
        ) : (
          <div className="faint">Программа пересобрана — менять под цели было нечего.</div>
        ))}

      {error && <div className="flag critical" style={{ marginBottom: 10 }}>{error}</div>}

      <button className="btn wide" disabled={busy} onClick={() => void apply()}>
        {busy ? 'Пересобираю…' : 'Пересобрать под цели'}
      </button>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Карточка активной цели                                                      */
/* -------------------------------------------------------------------------- */

function GoalCard({
  goal,
  exercise,
  busy,
  peekKey,
  onTogglePeek,
  addition,
  additionsFailed,
  onAdditionChanged,
  onAchieved,
  onAbandoned,
}: {
  goal: Goal
  exercise: ExerciseRef
  busy: boolean
  /** Какая техника раскрыта — помнит экран: открыта одна на весь экран. */
  peekKey: string | null
  onTogglePeek: (key: string) => void
  /** Доп. упражнение под цель; `null` — ответ ещё не пришёл. */
  addition: GoalAddition | null
  additionsFailed: boolean
  onAdditionChanged: () => Promise<void>
  onAchieved: () => void
  onAbandoned: () => void
}) {
  const goalKey = `goal-${goal.id}`
  // Доля от цели. Считать её здесь можно: это отношение двух уже полученных
  // от сервера чисел, а не новая метрика (движок ничего подобного не отдаёт).
  const share =
    goal.current_e1rm !== null && goal.target_1rm > 0
      ? (goal.current_e1rm / goal.target_1rm) * 100
      : null

  // Вниз, а не к ближайшему: 99.6% округлилось бы до «100%» рядом с открытой
  // целью, а взятой движок считает её только при current >= target — экран
  // объявил бы цель достигнутой раньше движка.
  const percent = share === null ? null : Math.floor(share)

  return (
    <div className="card">
      <div style={{ marginBottom: 10 }}>
        <ExerciseHead
          exercise={exercise}
          open={peekKey === goalKey}
          onToggle={() => onTogglePeek(goalKey)}
          title={<b>{exercise.name}</b>}
          aside={
            // Больше сотни не прячем: заниженная цифра при перевыполнении была
            // бы враньём. Обрезаем только полосу — она вылезла бы за дорожку.
            <span className="faint tnum">{percent === null ? '—' : `${percent}%`}</span>
          }
        />
      </div>

      <div className="row" style={{ alignItems: 'baseline', gap: 8 }}>
        <span className="big-number">{formatWeight(goal.current_e1rm)}</span>
        <span className="muted">
          из {formatWeight(goal.target_1rm)} кг
        </span>
      </div>
      <div className="faint" style={{ marginTop: -2 }}>
        текущий расчётный максимум на одно повторение
      </div>

      <div className="goal-bar" aria-hidden="true">
        <div className="goal-bar-fill" style={{ width: `${Math.min(100, share ?? 0)}%` }} />
      </div>

      {goal.current_e1rm === null && (
        <div className="faint" style={{ marginBottom: 10 }}>
          Максимум пока не посчитан — он появится после первых рабочих подходов в этом
          движении.
        </div>
      )}

      <PaceLine pace={goal.pace} />

      <ForecastLine forecast={goal.forecast} />

      {goal.target_date && (
        <div className="faint" style={{ marginTop: 8 }}>
          Твой срок: {formatGoalDate(goal.target_date)}
        </div>
      )}

      {addition !== null ? (
        <GoalAdditionBlock
          addition={addition}
          peekKey={peekKey}
          onTogglePeek={onTogglePeek}
          onChanged={onAdditionChanged}
        />
      ) : (
        additionsFailed && (
          <div className="ga faint">
            Доп. упражнение под цель не загрузилось — открой экран ещё раз.
          </div>
        )
      )}

      {/* Обе кнопки нейтральные: главная кнопка карточки — «Добавить в план»
          выше, и второй лаймовой рядом с ней быть не должно. */}
      <div className="row" style={{ marginTop: 14 }}>
        <button
          className="btn"
          style={{ flex: 1 }}
          disabled={busy}
          onClick={onAchieved}
        >
          Достигнута
        </button>
        <ConfirmButton
          className="btn danger"
          confirmLabel="Точно отказаться?"
          onConfirm={onAbandoned}
        >
          Отказаться
        </ConfirmButton>
      </div>
    </div>
  )
}

/**
 * Доп. упражнение под цель.
 *
 * Что предложить, в какой день, сколько подходов и почему, решает сервер
 * (`engine.goal_additions`), и тексты его. Экран показывает движение с
 * картинкой и одну кнопку: план принадлежит человеку, и без его «да» в нём
 * ничего не меняется. Если добавлять нельзя — цель взята, роста нет, дефицит,
 * коридор полон, — приходит причина, и она стоит на месте движения.
 */
function GoalAdditionBlock({
  addition,
  peekKey,
  onTogglePeek,
  onChanged,
}: {
  addition: GoalAddition
  peekKey: string | null
  onTogglePeek: (key: string) => void
  onChanged: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  // Состояние из ответа на последнее действие. Держится, пока экран не
  // перечитал список: не вышло перечитать — карточка всё равно показывает то,
  // что сервер уже сделал, а не зовёт повторить сделанное.
  const [answer, setAnswer] = useState<{ base: GoalAddition; value: GoalAddition } | null>(null)
  // Сообщение привязано к состоянию, о котором оно: пришло новое предложение
  // или пересборка переложила добавку — текст про прежнее уже не показывается.
  // Отказ сервера (`sig: null`) виден до следующего действия.
  const [notice, setNotice] = useState<{
    sig: string | null
    error: string | null
    flags: Flag[]
  } | null>(null)

  const shown = answer !== null && answer.base === addition ? answer.value : addition
  const sig = additionSig(shown)

  async function run(action: () => Promise<GoalAdditionResult>): Promise<void> {
    // Двойной тап отправил бы второй запрос: сервер его отобьёт, но человек
    // увидел бы отказ поверх успеха.
    if (busy) return
    setBusy(true)
    setNotice(null)
    try {
      const result = await action()
      haptic('medium')
      setAnswer({ base: addition, value: result.addition })
      setNotice({ sig: additionSig(result.addition), error: null, flags: result.flags })
      await onChanged()
    } catch (e) {
      const refused = e instanceof ApiError && e.status === 409
      setNotice({ sig: refused ? null : sig, error: describeAddition(e), flags: [] })
      // 409 — предложение устарело или добавлять стало нельзя; обрыв связи —
      // правка могла и дойти. В обоих случаях нужен свежий ответ сервера.
      if (refused || e instanceof NetworkError) await onChanged()
    } finally {
      setBusy(false)
    }
  }

  const { offer, applied } = shown
  const noticeBox =
    notice !== null && (notice.sig === null || notice.sig === sig) ? (
      <>
        <Flags flags={notice.flags} />
        {notice.error && <div className="flag critical">{notice.error}</div>}
      </>
    ) : null

  // Ключ просмотра — от движения, а не от номера в списке: иначе после
  // «Убрать» под тем же ключом раскрылась бы техника другого движения.
  if (shown.state === 'offer' && offer !== null) {
    const key = `add-${shown.goal_id}-offer-${offer.exercise.id}`
    return (
      <div className="ga">
        <div className="ga-head">
          <b>Доп. упражнение</b>
          <span className={`pill ${offer.advice === 'recommended' ? 'accent' : ''}`}>
            {offer.advice === 'recommended' ? 'рекомендую' : 'по желанию'}
          </span>
        </div>
        <ExerciseHead
          exercise={offer.exercise}
          open={peekKey === key}
          onToggle={() => onTogglePeek(key)}
          title={<div className="ex-name">{offer.exercise.name}</div>}
          sub={<div className="faint tnum">{offerScheme(offer)}</div>}
        />
        <div className="faint tnum ga-when">
          {offer.first_date !== null && <>Ближайшая: {formatGoalDate(offer.first_date)} · </>}
          тренировок с добавкой: {offer.sessions_left}
        </div>
        <div className="ga-reason">{offer.reason}</div>
        {offer.risks.map((risk) => (
          <div key={risk} className="flag warning">
            {risk}
          </div>
        ))}
        {noticeBox}
        <button
          className="btn primary wide"
          disabled={busy}
          onClick={() =>
            void run(() =>
              api.post<GoalAdditionResult>(`/goals/${addition.goal_id}/addition`, {
                exercise_id: offer.exercise.id,
                day_no: offer.day_no,
              }),
            )
          }
        >
          {busy ? 'Добавляю…' : 'Добавить в план'}
        </button>
      </div>
    )
  }

  if (shown.state === 'applied' && applied !== null) {
    // Под цель работает целое занятие, а не добавка к чужому дню — назвать
    // его «доп. упражнением» значило бы назвать не то, что стоит в плане.
    // Откуда движение в плане, говорит сервер (`source`).
    const fromGoalDay =
      applied.items.length > 0 && applied.items.every((item) => item.source === 'goal_day')
    return (
      <div className="ga">
        <div className="ga-head">
          <b>{fromGoalDay ? 'Доп. занятие' : 'Доп. упражнение'}</b>
          <span className="pill ok">в плане</span>
        </div>
        {applied.items.map((item) => {
          const key = `add-${shown.goal_id}-in-${item.exercise.id}-${item.day_label}`
          return (
            <div key={key} className="ga-item">
              <ExerciseHead
                exercise={item.exercise}
                open={peekKey === key}
                onToggle={() => onTogglePeek(key)}
                title={<div className="ex-name">{item.exercise.name}</div>}
                sub={
                  <div className="faint tnum">
                    «{item.day_label}» · {item.sets_per_week} подх. в неделю
                    {item.source === 'rebuild' ? ' · добавила пересборка' : ''}
                  </div>
                }
              />
            </div>
          )
        })}
        <div className="faint tnum ga-when">
          Впереди тренировок с добавкой: {applied.sessions_left}
        </div>
        {applied.risks.map((risk) => (
          <div key={risk} className="flag warning">
            {risk}
          </div>
        ))}
        {noticeBox}
        {applied.removal_note && <div className="faint ga-when">{applied.removal_note}</div>}
        <ConfirmButton
          className="btn wide"
          confirmLabel="Точно убрать из плана?"
          onConfirm={() =>
            void run(() =>
              api.delete<GoalAdditionResult>(`/goals/${addition.goal_id}/addition`),
            )
          }
        >
          {busy ? 'Убираю…' : fromGoalDay ? 'Убрать из доп. занятия' : 'Убрать добавку'}
        </ConfirmButton>
      </div>
    )
  }

  return (
    <div className="ga">
      <div className="faint">
        <b>Доп. упражнение.</b> {shown.message}
      </div>
      {noticeBox}
    </div>
  )
}

/**
 * Чем состояние карточки отличается от прежнего — для сообщений, привязанных
 * к состоянию. Это опознание, а не расчёт: числа лишь сравниваются.
 */
function additionSig(addition: GoalAddition): string {
  if (addition.offer !== null) {
    const o = addition.offer
    return `offer:${o.kind}:${o.exercise.id}:${o.day_no}:${o.sets}`
  }
  if (addition.applied !== null) {
    const items = addition.applied.items
      .map((i) => `${i.exercise.id}@${i.day_label}x${i.sets_per_week}`)
      .join(',')
    return `applied:${items}:${addition.applied.sessions_left}`
  }
  return `${addition.state}:${addition.message}`
}

/** Схема добавки одной строкой: день и подходы. Все числа — сервера. */
function offerScheme(offer: GoalOffer): string {
  const reps =
    offer.reps_min === offer.reps_max ? `${offer.reps_min}` : `${offer.reps_min}–${offer.reps_max}`
  // У процентного блока нагрузку задаёт процент, а не RPE: приписать RPE
  // значило бы показать цифру, по которой вес не считается. Процент — так же,
  // как в составе программы.
  const rpe =
    offer.percent_of_e1rm === null
      ? ` @ RPE ${Number(offer.target_rpe.toFixed(1))}`
      : ` · ${Math.round(offer.percent_of_e1rm * 100)}%`
  const sets = offer.kind === 'more_sets' ? `+${offer.sets} подх. × ${reps}` : `${offer.sets} × ${reps}`
  return `«${offer.day_label}» · ${sets}${rpe}`
}

/** Закрытая цель: без прогноза и без кнопок — это уже история, а не план. */
function ClosedGoalCard({ goal, name }: { goal: Goal; name: string }) {
  return (
    <div className="card tight dim">
      <div className="row between">
        <span>
          {name} · {formatWeight(goal.target_1rm)} кг
        </span>
        <span className={`pill ${goal.status === 'achieved' ? 'ok' : ''}`}>
          {goal.status === 'achieved' ? 'взята' : 'отказ'}
        </span>
      </div>
    </div>
  )
}

/**
 * Успеваем ли к сроку — и что с этим делает программа.
 *
 * Стоит ВЫШЕ прогноза, потому что отвечает на вопрос, который человек задаёт
 * первым: «я успеваю?». Текст приходит с сервера целиком — движок уже
 * посчитал темп, дату выхода и разницу со сроком, и пересказывать его
 * своими словами значило бы завести второй источник правды о числах.
 *
 * Отставание помечено предупреждением, а «нет роста» и «движения нет в
 * программе» — тоже: это разные болезни, но каждая требует действия. Ровный
 * ход и достигнутая цель не помечаются никак: хорошие новости не должны
 * выглядеть как тревога.
 */
function PaceLine({ pace }: { pace: GoalPace | null }) {
  if (pace === null) return null
  if (pace.status === 'on_track' || pace.status === 'achieved') {
    return <div className="faint">{pace.reason}</div>
  }
  if (pace.status === 'unknown') return null
  return <div className="flag warning">{pace.reason}</div>
}

/**
 * Строка прогноза.
 *
 * Три разных состояния, и ни одно не прячется (раздел 2, принцип 3):
 * прогноза нет вовсе, движок отказался его строить, движок его построил.
 */
function ForecastLine({ forecast }: { forecast: Forecast | null }) {
  if (forecast === null) {
    return (
      <div className="faint">Прогноз появится, когда наберётся история.</div>
    )
  }

  if (!forecast.has_forecast) {
    // Причина показывается дословно: движок формулирует её сам и знает,
    // почему отказался. Дату в этом случае не показываем вообще.
    return <div className="flag info">{forecast.reason}</div>
  }

  return (
    <div className="goal-forecast">
      {forecast.projected_date && (
        <div>
          Выход ориентировочно <b>{formatGoalDate(forecast.projected_date)}</b>
        </div>
      )}
      {forecast.rate_kg_per_month !== null && (
        <div className="muted">
          Темп: {formatWeight(forecast.rate_kg_per_month)} кг/мес
        </div>
      )}
      <div className="faint">достоверность R² = {forecast.r_squared.toFixed(2)}</div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Создание цели                                                               */
/* -------------------------------------------------------------------------- */

function NewGoalForm({
  openGoals,
  nameOf,
  minDate,
  onCancel,
  onCreated,
}: {
  openGoals: Goal[]
  nameOf: (exerciseId: number) => string
  minDate: string | null
  onCancel: () => void
  onCreated: () => Promise<void>
}) {
  // Справочник для целей приходит отдельной ручкой и вместе с цифрами по
  // каждому движению: текущий максимум, предложенная планка и объяснение,
  // откуда она взялась. Собрать это на клиенте нельзя — считает движок.
  const options = useAsync(() => api.get<GoalThemeOut[]>('/goals/options'), [])
  const themes = options.data ?? []

  const [themeCode, setThemeCode] = useState<string | null>(null)
  const [picked, setPicked] = useState<GoalOption | null>(null)
  // Просмотр движения встаёт на место списка — как в выборе замены: список
  // прокручивается в своём блоке, и высокий кадр внутри него ушёл бы за край.
  const [peek, setPeek] = useState<GoalOption | null>(null)
  // Прокрутка списка движений внутри его блока. Просмотр встаёт на место
  // списка, и вернувшийся список смонтировался бы прокрученным в начало:
  // движение, выбранное из просмотра, осталось бы за нижним краем блока, а
  // поле веса получило бы цифру для движения, которого не видно.
  const movesRef = useRef<HTMLDivElement>(null)
  const movesScroll = useRef(0)
  useLayoutEffect(() => {
    if (peek === null && movesRef.current) movesRef.current.scrollTop = movesScroll.current
  }, [peek])
  const [target, setTarget] = useState<number>(DEFAULT_TARGET)
  const [date, setDate] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const theme = themes.find((t) => t.code === themeCode) ?? themes[0] ?? null

  /** Выбрали движение — поле цели встаёт на цифру сервера, а не на 100 кг. */
  function choose(option: GoalOption) {
    setPicked(option)
    setTarget(startingTarget(option))
  }

  // Сервер закрывает прежнюю цель по тому же упражнению сам. Человек обязан
  // узнать об этом ДО нажатия, а не после — иначе он потеряет цель молча.
  const conflicting =
    picked === null ? undefined : openGoals.find((g) => g.exercise_id === picked.exercise_id)

  async function submit() {
    if (picked === null) return
    setBusy(true)
    setError(null)
    try {
      await api.post<Goal>('/goals', {
        exercise_id: picked.exercise_id,
        target_1rm: target,
        target_date: date === '' ? null : date,
      })
      haptic('medium')
      await onCreated()
    } catch (e) {
      setError(describe(e, 'Не удалось поставить цель'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 12 }}>
        <b>Новая цель</b>
        <button className="btn ghost" style={{ minHeight: 40, padding: '0 12px' }} onClick={onCancel}>
          Отмена
        </button>
      </div>

      {options.loading && options.data === null && <Loader label="Собираю справочник…" />}

      {options.error !== null && options.data === null && (
        <ErrorBox error={options.error} onRetry={() => void options.reload()} />
      )}

      {theme !== null && (
        <>
          <div className="faint" style={{ marginBottom: 6 }}>Что качаем</div>
          <div className="goal-picker">
            {themes.map((t) => (
              <button
                key={t.code}
                type="button"
                className={`pill ${t.code === theme.code ? 'accent' : ''}`}
                onClick={() => {
                  setThemeCode(t.code)
                  setPicked(null)
                  setPeek(null)
                  movesScroll.current = 0
                }}
              >
                {t.title}
              </button>
            ))}
          </div>
          <div className="faint" style={{ marginTop: 6 }}>{theme.hint}</div>

          <div className="faint" style={{ margin: '14px 0 6px' }}>Движение</div>
          {theme.options.length === 0 ? (
            <div className="faint">
              В этой теме нечего предложить: движения убраны из выбора или исключены
              из-за боли.
            </div>
          ) : peek !== null ? (
            // Выбор из просмотра только выбирает движение: цель ставится
            // отдельной кнопкой ниже, так что и отсюда ничего не случится
            // без явного «Поставить цель».
            <ExercisePeek
              exercise={{ id: peek.exercise_id, name: peek.name, is_custom: peek.is_custom }}
              pickLabel="Цель на это движение"
              disabled={false}
              onBack={() => setPeek(null)}
              onPick={() => {
                // То же движение — просмотр лишь подтвердил выбор. Введённый вес
                // не трогаем: поле скрыто, и он молча вернулся бы к цифре сервера.
                if (picked?.exercise_id !== peek.exercise_id) choose(peek)
                setPeek(null)
              }}
            />
          ) : (
            <>
              <div className="goal-moves" ref={movesRef}>
                {theme.options.map((option) => (
                  <MovementRow
                    key={option.exercise_id}
                    option={option}
                    selected={picked?.exercise_id === option.exercise_id}
                    onPick={() => choose(option)}
                    onPeek={() => {
                      movesScroll.current = movesRef.current?.scrollTop ?? 0
                      setPeek(option)
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Пока открыт просмотр, поле цели скрыто: иначе кадры и степпер веса
          спорили бы за один экран, и непонятно, к какому движению цифра. */}
      {picked !== null && peek === null && (
        <>
          <div style={{ margin: '16px 0' }}>
            <Stepper
              value={target}
              onChange={setTarget}
              step={2.5}
              min={TARGET_MIN}
              max={TARGET_MAX}
              unit="кг"
              decimals={1}
              label={
                picked.external_load_only
                  ? 'Целевой вес СВЕРХ собственного на одно повторение'
                  : 'Целевой вес на одно повторение'
              }
            />
          </div>

          {/* Откуда взялась цифра в поле — словами движка. Это не украшение:
              предложенная планка посчитана по собственному темпу человека, а
              когда темпа нет, он обязан знать, что цифру ставит сам. */}
          {picked.basis !== '' && (
            <div className="faint" style={{ marginTop: -6, marginBottom: 12 }}>
              {picked.basis}
            </div>
          )}

          {picked.external_load_only && (
            <div className="faint" style={{ marginBottom: 12 }}>
              Это движение с весом тела: цель считается по весу, который ты довешиваешь
              сверху. Ноль — чистые подтягивания.
            </div>
          )}

          <div className="faint" style={{ marginBottom: 6 }}>Срок — если он у тебя есть</div>
          {/* Единственное место в приложении с системным вводом: календарь всё
              равно лучше, чем шаговый ввод по трём числам. Нижнюю границу даёт
              сервер (дата из свежего `/today`) — своей «сегодня» клиент не
              выдумывает, а вчерашнюю из кеша сюда не пускает. */}
          <input
            className="goal-date"
            type="date"
            value={date}
            min={minDate ?? undefined}
            onChange={(e) => setDate(e.target.value)}
          />

          {conflicting ? (
            <div className="flag warning" style={{ marginTop: 14 }}>
              По движению «{nameOf(conflicting.exercise_id)}» уже стоит цель{' '}
              {formatWeight(conflicting.target_1rm)} кг. Новая её закроет — прежняя уйдёт
              в историю как цель, от которой отказались.
            </div>
          ) : (
            <div className="faint" style={{ marginTop: 14 }}>
              Открытая цель по упражнению может быть одна: новая цель на то же движение
              закрывает предыдущую.
            </div>
          )}

          {/* Иначе человек поставит цель на бицепс, не увидит изменений в
              плане и решит, что цель ничего не значит. Значит — но с момента
              пересборки: переписывать уже начатый мезоцикл цель не вправе. */}
          <div className="faint" style={{ marginTop: 8 }}>
            Цель влияет и на программу: в карточке цели появится доп. упражнение под
            неё, а при пересборке движок поставит это движение в план.
          </div>

          {error && <div className="flag critical" style={{ marginTop: 10 }}>{error}</div>}

          <button
            className="btn primary wide big"
            style={{ marginTop: 14 }}
            disabled={busy}
            onClick={() => void submit()}
          >
            {busy ? 'Сохраняю…' : 'Поставить цель'}
          </button>
        </>
      )}
    </div>
  )
}

/**
 * Строка движения в выборе цели.
 *
 * Текущий максимум показывается прямо здесь: именно он отвечает на вопрос
 * «а с чего я начинаю». Движение без истории не прячется — цель на то, чего
 * человек ещё не делал, законна, — но и не притворяется измеренным.
 */
function MovementRow({
  option,
  selected,
  onPick,
  onPeek,
}: {
  option: GoalOption
  selected: boolean
  onPick: () => void
  onPeek: () => void
}) {
  // Раньше строка была одной кнопкой. Миниатюру в неё не вложить: кнопка в
  // кнопке — невалидная разметка, и в iOS тап ушёл бы во внешнюю, то есть
  // «посмотреть» стало бы «выбрать». Поэтому это соседи, как в выборе замены.
  return (
    <div className="goal-move-row">
      <ExerciseThumb
        exercise={{ id: option.exercise_id, name: option.name, is_custom: option.is_custom }}
        onPeek={onPeek}
      />
      <button type="button" className={`goal-move${selected ? ' selected' : ''}`} onClick={onPick}>
        <span className="goal-move-name">
          {option.name}
          {option.is_custom && <span className="pill">своё</span>}
          {option.has_open_goal && <span className="pill accent">цель стоит</span>}
        </span>
        <span className="faint tnum">
          {option.current_e1rm === null
            ? 'нет истории'
            : `сейчас ${formatWeight(option.current_e1rm)} кг`}
        </span>
      </button>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Мелочи                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Дата с годом, когда он не текущий.
 *
 * `formatDate` даёт «14 марта» — для прогноза этого мало: выход на вес часто
 * приходится на следующий год, и без года дата читается как ближайшая.
 */
function formatGoalDate(iso: string): string {
  const base = formatDate(iso)
  // «сегодня», «вчера», «завтра» — в них года нет по смыслу, а 31 декабря
  // приписка превратила бы их в «завтра 2027». Относительные ответы
  // `formatDate` отличаются от дат тем, что в них нет ни одной цифры.
  if (!/\d/.test(base)) return base
  const year = Number(iso.slice(0, 4))
  return Number.isFinite(year) && year !== new Date().getFullYear()
    ? `${base} ${year}`
    : base
}

/**
 * Текст ошибки для пользователя.
 *
 * Разница между «нет связи» и «сервер отказал» здесь принципиальна: цель, в
 * отличие от подхода, в офлайн-очередь не попадает — при обрыве связи она
 * просто не сохранена, и человек должен это понимать.
 */
function describe(e: unknown, fallback: string): string {
  if (e instanceof NetworkError) return 'Нет связи — цель не сохранена. Попробуй ещё раз.'
  if (e instanceof Error && e.message) return e.message
  return fallback
}

/**
 * То же для правки плана под цель. При обрыве связи честно «не знаю»: запрос
 * мог дойти и выполниться, а ответ — потеряться по дороге.
 */
function describeAddition(e: unknown): string {
  if (e instanceof NetworkError) {
    return 'Нет связи — не удалось узнать, изменился ли план. Проверь, когда связь вернётся.'
  }
  if (e instanceof Error && e.message) return e.message
  return 'Не удалось изменить план'
}
